<template>
  <div
    ref="dropdownEl"
    class="dropdown-wrapper tw:relative tw:flex tw:min-h-8 tw:items-center tw:border-y-0 tw:border-l-0 tw:border-r tw:border-solid tw:border-r-[#ddd]"
  >
    <button
      :aria-activedescendant="activeDescendantId"
      :aria-controls="open ? menuId : undefined"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-label="dropdownAriaLabel"
      class="dropdown tw:flex tw:appearance-none tw:items-center tw:border-0 tw:bg-transparent tw:cursor-pointer tw:[font:inherit] tw:text-inherit tw:text-left tw:rounded-sm"
      data-testid="universal-search-dropdown-trigger"
      type="button"
      @click="toggleOpen"
      @keydown="onKeydown"
    >
      <div class="tw:gap-2 tw:flex tw:items-center">
        <BaseIcon
          v-if="icons[selectedOption]"
          class="tw:text-2xl"
          :icon="icons[selectedOption]"
        />
        <span class="dropdown-label-text">
          {{ labels[selectedOption] }}
        </span>
      </div>

      <BaseIcon icon="angle-down" />
    </button>

    <ul
      v-show="open"
      :id="menuId"
      class="listbox tw:absolute tw:top-full tw:left-0 tw:z-10 tw:m-0 tw:mt-1 tw:flex tw:min-w-40 tw:list-none tw:flex-col tw:rounded tw:bg-white tw:p-0 tw:shadow-lg"
      data-testid="universal-search-dropdown-listbox"
      role="listbox"
    >
      <li
        v-for="(label, key) in labels"
        :id="optionId(key)"
        :key="key"
        :aria-selected="selectedOption === key"
        class="option tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:whitespace-nowrap tw:px-4 tw:py-3"
        :class="{ 'is-active': activeKey === key }"
        :data-testid="`universal-search-dropdown-option-${key}`"
        role="option"
        @click="selectOption(key)"
        @mousemove="activeKey = key"
      >
        <span class="tw:inline-flex tw:w-5 tw:shrink-0 tw:justify-center">
          <BaseIcon
            v-if="icons[key]"
            :icon="icons[key]"
          />
        </span>
        <span class="text-paragraph">
          {{ label }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";
import { ref, useId } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseIcon } from "shared/components/base";
import { useClickOutside } from "shared/composables/useClickOutside";
import { useUserStore } from "shared/stores/user";

const ICONS = {
  all: "globe",
  streams: "search",
};

export default {
  name: "UniversalSearchDropdown",
  components: { BaseIcon },
  props: {
    modelValue: {
      type: String,
      required: true,
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const userStore = useUserStore();
    const { adminUserEnabled, customerViewEnabled } = storeToRefs(userStore);
    const menuId = `universal-search-dropdown-menu-${useId()}`;

    const open = ref(false);
    const activeKey = ref(null);
    const dropdownEl = ref(null);

    useClickOutside(
      dropdownEl,
      () => {
        open.value = false;
      },
      { active: open }
    );

    return {
      activeKey,
      adminUserEnabled,
      customerViewEnabled,
      dropdownEl,
      icons: ICONS,
      menuId,
      open,
    };
  },
  computed: {
    activeDescendantId() {
      if (!this.open || !this.activeKey) return undefined;

      return this.optionId(this.activeKey);
    },
    dropdownAriaLabel() {
      // The label is only on screen when the bar is wide enough for it, so the
      // scope has to be in the accessible name either way.
      const genericLabel = getLocaleText("universal_search_dropdown.label");
      const selectedLabel = this.labels[this.selectedOption];

      return selectedLabel ? `${genericLabel}: ${selectedLabel}` : genericLabel;
    },
    labels() {
      const labels = {
        all: this.$t("universal_search_dropdown.all_label"),
        streams: this.$t("universal_search_dropdown.streams_label"),
      };

      // admin users and only those with the feature and permission enabled
      if (!this.canSearchAvailableContent) delete labels.all;

      return labels;
    },
    optionKeys() {
      return Object.keys(this.labels);
    },
    selectedOption: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    canSearchAvailableContent() {
      if (this.adminUserEnabled && !this.customerViewEnabled) return true;

      return (
        this.$features.has("has_search_available_content") &&
        this.$permissions.has("can_search_available_content")
      );
    },
  },
  watch: {
    open(isOpen) {
      if (!isOpen) {
        this.activeKey = null;

        return;
      }

      // Keyboard handlers seed activeKey before flipping `open`; only
      // initialize here for the click/programmatic open path.
      if (this.activeKey !== null) return;

      this.activeKey = this.optionKeys.includes(this.selectedOption)
        ? this.selectedOption
        : (this.optionKeys[0] ?? null);
    },
  },
  methods: {
    optionId(key) {
      return `${this.menuId}-option-${key}`;
    },
    toggleOpen() {
      this.open = !this.open;
    },
    stepActiveKey(direction) {
      const keys = this.optionKeys;

      if (!keys.length) return;

      const currentIndex = keys.indexOf(this.activeKey);
      const nextIndex = (currentIndex + direction + keys.length) % keys.length;

      this.activeKey = keys[nextIndex];
    },
    openMenuAt(key) {
      this.activeKey = key;
      this.open = true;
    },
    onKeydown(event) {
      const keys = this.optionKeys;

      if (!keys.length) return;

      const first = keys[0];
      const last = keys[keys.length - 1];
      const { key } = event;

      if (!this.open) {
        if (key === "ArrowDown" || key === "Enter" || key === " ") {
          event.preventDefault();

          this.openMenuAt(
            this.optionKeys.includes(this.selectedOption)
              ? this.selectedOption
              : first
          );
        } else if (key === "ArrowUp") {
          event.preventDefault();
          this.openMenuAt(last);
        }

        return;
      }

      switch (key) {
        case "ArrowDown":
          event.preventDefault();
          this.stepActiveKey(1);

          return;
        case "ArrowUp":
          event.preventDefault();
          this.stepActiveKey(-1);

          return;
        case "Home":
          event.preventDefault();
          this.activeKey = first;

          return;
        case "End":
          event.preventDefault();
          this.activeKey = last;

          return;
        case "Enter":
        case " ":
          event.preventDefault();
          if (this.activeKey) this.selectOption(this.activeKey);

          return;
        case "Escape":
          event.preventDefault();
          this.open = false;

          return;
        case "Tab":
          this.open = false;

          break;
        default:
          break;
      }
    },
    selectOption(option) {
      this.selectedOption = option;
      this.open = false;

      this.$track("Changed Universal Search Scope", {
        scope: option,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown {
  gap: 16px;
  margin-inline: 16px;
  padding: 0 16px 0 0;

  &:focus-visible {
    outline: 2px solid var(--s-color-primary);
    outline-offset: 2px;
  }
}

.option {
  &:hover,
  &.is-active {
    background: #ddd;
  }
}

@container universal-search-bar (width <= 500px) {
  .dropdown-label-text {
    display: none;
  }

  .dropdown {
    gap: 4px;
    margin-inline: 8px;
    padding-right: 8px;
  }
}
</style>
